<div class="agent-commissions-list-container">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Agent Voucher Commissions</h1>
        <p>View and manage agent commissions from voucher sales</p>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="filters-section">
    <div class="filter-header">
      <h3><i class="fas fa-filter"></i> Filter Commissions</h3>
      <button class="clear-filters-btn" (click)="clearFilters()" [disabled]="!hasActiveFilters() || loading">
        <i class="fas fa-times"></i> Clear Filters
      </button>
    </div>

    <div class="filters-grid">
      <div class="filter-group">
        <label for="dateFrom">From Date:</label>
        <input type="date" id="dateFrom" [(ngModel)]="filters.fromDate" (change)="onFilterChange()"
          class="filter-input">
      </div>

      <div class="filter-group">
        <label for="dateTo">To Date:</label>
        <input type="date" id="dateTo" [(ngModel)]="filters.toDate" (change)="onFilterChange()" class="filter-input">
      </div>

      <div class="filter-group">
        <label for="agentFilter">Agent:</label>
        <select id="agentFilter" [(ngModel)]="filters.agentId" (change)="onFilterChange()" class="filter-select">
          <option value="">All Agents</option>
          <option *ngFor="let agent of agents" [value]="agent.agentId">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label for="settledFilter">Status:</label>
        <select id="settledFilter" [(ngModel)]="filters.isSettled" (change)="onFilterChange()" class="filter-select">
          <option [ngValue]="null">All</option>
          <option [ngValue]="true">Settled</option>
          <option [ngValue]="false">Unsettled</option>
        </select>
      </div>

      <div class="filter-actions">
        <button class="apply-filters-btn" (click)="applyFilters()" [disabled]="loading">
          <i class="fas fa-search"></i> apply Filters
        </button>
      </div>
    </div>
  </div>

  <!-- Success Message -->
  <div *ngIf="successMessage" class="success-message">
    <i class="fas fa-check-circle mr-2"></i>
    {{ successMessage }}
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <i class="fas fa-exclamation-circle mr-2"></i>
    {{ errorMessage }}
  </div>

  <!-- Actions Bar -->
  <div *ngIf="!loading && selectedCommissions.size > 0" class="actions-bar">
    <div class="selection-info">
      <span class="count">{{ selectedCommissions.size }}</span> commissions selected
      <span class="total-label">Total:</span>
      <span class="total-value">{{ formatCurrency(getSelectedTotal()) }}</span>
    </div>
    <button class="batch-settle-btn" (click)="openSettlementModal()">
      <i class="fas fa-handshake"></i> Settle Selected
    </button>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="loading-spinner"></div>
    <span class="loading-text">Loading commissions...</span>
  </div>

  <!-- Results Summary -->
  <div *ngIf="!loading && commissions.length > 0" class="results-summary">
    <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{
      totalCount }} records</p>
  </div>

  <!-- Desktop Table View -->
  <div *ngIf="!loading && commissions.length > 0" class="desktop-table-container">
    <div class="table-wrapper">
      <table class="commissions-table">
        <thead>
          <tr>
            <th class="checkbox-cell">
              <input type="checkbox" (change)="toggleAll($event)" [checked]="isAllSelected()">
            </th>
            <th>Agent Name</th>
            <th>Collection Date</th>
            <th>Collection ID</th>
            <th>Grand Total</th>
            <th>Commission</th>
            <th>Status</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let commission of commissions" class="commission-row"
            [class.selected]="selectedCommissions.has(commission.scrapCollectionId)">
            <td class="checkbox-cell">
              <input type="checkbox" [checked]="selectedCommissions.has(commission.scrapCollectionId)"
                [disabled]="commission.isAgentVoucherCommissionSettled"
                (change)="toggleSelection(commission.scrapCollectionId)">
            </td>
            <td class="agent-name">{{ commission.agentName || 'N/A' }}</td>
            <td class="date">{{ formatDate(commission.collectionDate) }}</td>
            <td class="collection-id">{{ commission.collectionId }}</td>
            <td class="grand-total">{{ formatCurrency(commission.grandTotal) }}</td>
            <td class="commission">
              <div class="commission-value">{{ formatCurrency(commission.agentVoucherCommission) }}</div>
              <div class="commission-percentage text-xs text-gray-400">({{ commission.agentVoucherCommissionPercentage
                }}%)</div>
            </td>
            <td class="status">
              <span class="status-badge" [class.status-settled]="commission.isAgentVoucherCommissionSettled"
                [class.status-unsettled]="!commission.isAgentVoucherCommissionSettled">
                {{ commission.isAgentVoucherCommissionSettled ? 'Settled' : 'Unsettled' }}
              </span>
            </td>
            <td class="notes">{{ commission.agentVoucherCommissionNotes || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- No Data State -->
  <div *ngIf="!loading && commissions.length === 0" class="no-data-state">
    <div class="no-data-content">
      <i class="fas fa-coins"></i>
      <h3>No Commissions Found</h3>
      <p>No agent commissions match your current filters.</p>
    </div>
  </div>

  <!-- Pagination -->
  <div *ngIf="!loading && commissions.length > 0" class="pagination-container">
    <app-pagination [page]="currentPage" [totalPages]="totalPages" [pageSize]="pageSize"
      (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- Settlement Modal -->
  <div *ngIf="showSettlementModal" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h2>Process Settlement</h2>
        <button class="modal-close-btn" (click)="closeSettlementModal()">&times;</button>
      </div>
      <div class="modal-body">
        <div class="settlement-summary">
          <div class="summary-item">
            <span class="label">Commissions Count:</span>
            <span class="value">{{ selectedCommissions.size }}</span>
          </div>
          <div class="summary-item total">
            <span class="label">Total Amount:</span>
            <span class="value">{{ formatCurrency(getSelectedTotal()) }}</span>
          </div>
        </div>

        <div class="form-group">
          <label for="settlementNotes">Settlement Notes (Optional):</label>
          <textarea id="settlementNotes" [(ngModel)]="settlementNotes"
            placeholder="Enter any notes about this settlement batch..." class="form-textarea"></textarea>
        </div>

        <div class="modal-actions">
          <button class="cancel-btn" (click)="closeSettlementModal()" [disabled]="isBatchProcessing">Cancel</button>
          <button class="confirm-btn" (click)="processSettlement()" [disabled]="isBatchProcessing">
            <span *ngIf="!isBatchProcessing"><i class="fas fa-check"></i> Complete Settlement</span>
            <span *ngIf="isBatchProcessing"><i class="fas fa-spinner fa-spin"></i> Processing...</span>
          </button>
        </div>
      </div>
    </div>
  </div>

</div>